import {computed, defineComponent, type PropType, ref, watch} from 'vue';
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";
import {DocumentType, type Invoice, PaymentMethod} from "@/api/enterprise-gateway-api.proxy.ts";
import {useFormatting} from "@/composables/currency-format.ts";
import {type InvoicesResponse, useInvoicingStore} from "@/app/stores/invoicing-store.ts";

export default defineComponent({
    name: 'AegAdminInvoices',
    props: {
        invoices: {
            type: Object as PropType<InvoicesResponse>,
            required: true
        },
        isLoading: {
            type: Boolean,
            required: true,
            default: false
        },
        hideHeaderPagination: {
            type: Boolean,
            required: false,
            default: false
        }
    }, 
    emits: ['change-page'],
    setup: function (props, {emit}) {
        const {formatCurrency, formatDate} = useFormatting();
        const invoiceStore = useInvoicingStore();
        const pagedInvoices = ref<PagedResult<Invoice>>(PagedResult.createEmpty<Invoice>());
        const loading = ref<boolean>(false);
        const emptyState = ref<boolean>(false);
        const downloadFileUriLoading = ref<boolean>(false);
        const downloadFileUri = ref<string | undefined>('');

        const changePage = async (pageNumber: number) => {
            emit('change-page', pageNumber);
        }

        const showDownloadLink = computed((): boolean => {
            return !!pagedInvoices.value && pagedInvoices.value.items.length > 0;
        });
        
        const invoicesChanged = (newInvoices: InvoicesResponse) => {
            if(!newInvoices) return;
            pagedInvoices.value = newInvoices.pagedResult;
            emptyState.value = newInvoices.pagedResult.items.length === 0;
            downloadFileUri.value = newInvoices.downloadUrl
        }

        const getDocumentTypeLabel = (documentType?: DocumentType): string => {
            return invoiceStore.getDocumentTypeLabel(documentType);
        };

        const getInvoiceTypeIcon = (documentType?: DocumentType, paymentMethod?: PaymentMethod): string => {
            return invoiceStore.getInvoiceTypeIcon(documentType, paymentMethod);
        };

        const isTopUpType = (documentType?: DocumentType): boolean => {
            return invoiceStore.isTopUpType(documentType);
        };
        
        watch(() => props.invoices, invoicesChanged, {immediate: true, deep: true});
        watch(() => props.isLoading, (newValue) => { loading.value = newValue }, {immediate: true, deep: true});

        return {
            loading,
            emptyState,
            showDownloadLink,
            pagedInvoices,
            downloadFileUri,
            downloadFileUriLoading,
            getDocumentTypeLabel,
            getInvoiceTypeIcon,
            isTopUpType,
            formatCurrency,
            formatDate,
            changePage
        };
    }
})
